// --- React libraries ---
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Pin } from 'lucide-react';

// --- shared components ---
import { MAIN_NAV_SECTIONS, FOOTER_NAV_SECTIONS, type NavSection } from './navigationSections';
import { CATEGORY_ORDER, NAV_ITEMS } from '../constants';

// --- assets ---
import libertasLogo from '../assets/libertas-logo.jpg';

interface SidebarProps {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  onSectionHover: (sectionId: string, anchorRect: DOMRect) => void;
  onSectionLeave: () => void;
  activeNavSectionId: string | null;
  onToggleAssistant: () => void;
  onNavigate?: (path: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  toggleSidebar,
  onSectionHover,
  onSectionLeave,
  activeNavSectionId,
  onToggleAssistant,
  onNavigate: _onNavigate
}) => {
  // Load pinned items from localStorage
  const [pinnedItems, setPinnedItems] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('grid-pinned-items');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      // Failed to load pinned items from localStorage - using empty set
      return new Set();
    }
  });

  // Listen for storage changes to sync pinned items across components
  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const saved = localStorage.getItem('grid-pinned-items');
        setPinnedItems(saved ? new Set(JSON.parse(saved)) : new Set());
      } catch {
        // Failed to sync pinned items - using empty set
      }
    };

    window.addEventListener('storage', handleStorageChange);
    // Also listen for custom event from same window
    window.addEventListener('pinnedItemsChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('pinnedItemsChanged', handleStorageChange);
    };
  }, []);

  // Get pinned nav items
  const pinnedNavItems = NAV_ITEMS.filter(item => pinnedItems.has(item.id));

  // Create a virtual "Pinned" section
  const pinnedSection: NavSection | null = pinnedNavItems.length > 0 ? {
    id: 'pinned',
    label: 'Pinned',
    items: pinnedNavItems,
    placement: 'main' as const,
    icon: Pin
  } : null;

  const renderSectionButton = (section: NavSection, variant: 'default' | 'assistant' = 'default') => {
    const Icon = section.icon;
    const isActive = activeNavSectionId === section.id;
    const isAssistantVariant = variant === 'assistant';

    const baseStateClasses = isAssistantVariant
      ? 'border-primary/60 bg-primary/5 text-primary hover:bg-primary/10'
      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border/60';

    const activeStateClasses = isActive
      ? 'border-primary/60 bg-primary/10 text-primary'
      : baseStateClasses;

    // For assistant variant, use different handlers
    const buttonProps = isAssistantVariant ? {
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        onToggleAssistant();
      }
    } : {
      'data-nav-overlay-trigger': true,
      onMouseEnter: (event: React.MouseEvent<HTMLButtonElement>) => {
        const rect = (event.currentTarget as HTMLButtonElement).getBoundingClientRect();
        onSectionHover(section.id, rect);
      },
      onFocus: (event: React.FocusEvent<HTMLButtonElement>) => {
        const rect = (event.currentTarget as HTMLButtonElement).getBoundingClientRect();
        onSectionHover(section.id, rect);
      },
      onMouseLeave: () => onSectionLeave(),
      onBlur: () => onSectionLeave(),
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        const rect = (event.currentTarget as HTMLButtonElement).getBoundingClientRect();
        onSectionHover(section.id, rect);
      }
    };

    return (
      <button
        key={section.id}
        {...buttonProps}
        className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} gap-3 px-3 ${
          isAssistantVariant ? 'py-3 rounded-2xl' : 'py-2 rounded-swm-panel'
        } border transition-colors text-sm font-medium ${activeStateClasses}`}
      >
        <div className="flex items-center gap-3">
          <Icon size={18} className={isAssistantVariant || isActive ? 'text-primary' : 'text-muted-foreground'} />
          {!isCollapsed && (
            <span className={`text-sm font-semibold leading-tight ${isAssistantVariant ? 'text-primary' : ''}`}>
              {section.label}
            </span>
          )}
        </div>
        {!isCollapsed && (
          isAssistantVariant ? (
            <span className="text-xs font-semibold text-primary">Launch</span>
          ) : (
            <ChevronRight size={16} className="text-muted-foreground" />
          )
        )}
      </button>
    );
  };

  const HalfDivider = () => (
    <div className="flex justify-center py-1" aria-hidden="true">
      <div className="h-px w-4/5 bg-secondary/30" />
    </div>
  );

  const moduleCenterSection = MAIN_NAV_SECTIONS.find(section => section.id === 'module-center');
  const dashboardSection = MAIN_NAV_SECTIONS.find(section => section.id === 'dashboard');
  const reportSection = MAIN_NAV_SECTIONS.find(section => section.id === 'reports');
  const categorySections = CATEGORY_ORDER.reduce<NavSection[]>((ordered, category) => {
    const match = MAIN_NAV_SECTIONS.find(section => section.category === category);
    if (match) ordered.push(match);
    return ordered;
  }, []);
  const assistantSection = FOOTER_NAV_SECTIONS.find(section => section.id === 'ai');

  return (
    <aside 
      className={`
        fixed left-0 top-0 h-full bg-background z-40
        transition-all duration-swm ease-out flex flex-col
        ${isCollapsed ? 'w-[72px]' : 'w-[240px]'}
      `}
    >
      {/* Header / Toggle */}
      <div className="h-14 flex items-center justify-between px-4">
        {!isCollapsed && (
           <div className="flex items-center font-bold text-lg tracking-tight animate-in fade-in duration-300">
             <img src={libertasLogo} alt="Libertas" className="w-26 h-26 object-contain mt-5" />
             <span className="text-foreground">Sens<span className="text-blue-400">AI</span></span>
           </div>
        )}
        <button
          onClick={toggleSidebar}
          className={`
            p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20
            ${isCollapsed ? 'mx-auto' : ''}
          `}
        >
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4 px-3 space-y-2 scrollbar-hide">
        <HalfDivider />

        {/* Pinned Section */}
        {pinnedSection && (
          <>
            {renderSectionButton(pinnedSection)}
            <HalfDivider />
          </>
        )}

        {moduleCenterSection && (
          <>
            {renderSectionButton(moduleCenterSection)}
            <HalfDivider />
          </>
        )}
        {dashboardSection && (
          <>
            {renderSectionButton(dashboardSection)}
            <HalfDivider />
          </>
        )}
        {categorySections.length > 0 && (
          <>
            {categorySections.map(section => renderSectionButton(section))}
            <HalfDivider />
          </>
        )}
        {reportSection && renderSectionButton(reportSection)}
      </div>

      {/* Footer Sections */}
      <div className="p-3 space-y-2">
        {assistantSection && renderSectionButton(assistantSection, 'assistant')}
      </div>
    </aside>
  );
};

export default Sidebar;